import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, CheckCircle2, ClipboardList, Clock, Copy, CreditCard, Download, FileText, Gift, Hourglass, Loader2, Lock,
  Package, PackageCheck, Truck, XCircle,
} from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatPrice, formatDateTime, PAYMENT_LABELS, ORDER_STATUS } from "@/lib/format";
import { RichTextBlocks } from "@/components/RichText";
import OrderReceiptButton from "@/components/OrderReceiptButton";
import OrderReviewsCard from "@/components/OrderReviewsCard";

const Card = ({ icon: Icon, title, children }) => (
  <div className="rounded-2xl border border-line bg-white p-5">
    <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
      <Icon className="h-4 w-4 text-brand" /> {title}
    </p>
    <div className="mt-3">{children}</div>
  </div>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-3 py-1 text-xs">
    <span className="text-slate-500">{label}</span>
    <span className="text-right font-semibold text-ink">{value || "—"}</span>
  </div>
);

export default function AccountOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/my/orders/${id}`);
      setOrder(data);
    } catch (err) {
      setError(apiError(err, "Commande introuvable"));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const copy = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copié !");
    } catch (e) {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  const confirmReceipt = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post(`/my/orders/${id}/confirm-receipt`, { comment });
      setOrder(data);
      setComment("");
      toast.success("Merci ! La réception de votre commande est confirmée.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setConfirming(false);
    }
  };

  if (error) {
    return (
      <div className="rounded-2xl border border-line bg-white p-10 text-center">
        <p className="text-sm font-bold text-ink">{error}</p>
        <Link to="/compte/commandes" className="mt-4 inline-block rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-white">
          Retour à mes commandes
        </Link>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const status = ORDER_STATUS[order.status] || ORDER_STATUS.processing;
  const delivery = order.delivery;
  const delivered = delivery?.status === "delivered";
  const keys = (order.licenses || []).filter((l) => l.key_value && l.status === "delivered");
  const legacyMessages = (order.licenses || []).filter((l) => !l.key_value && l.status === "delivered" && l.admin_message);
  const allFields = (order.licenses || []).filter((l) => (l.fields || []).length > 0);

  return (
    <div className="space-y-5" data-testid="account-order-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/compte/commandes" data-testid="back-to-my-orders"
                className="flex h-9 w-9 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="font-display text-lg font-extrabold text-ink sm:text-xl">
              Détails de la commande {order.order_number}
            </h1>
            <p className="text-xs text-slate-500">
              Commandée le {formatDateTime(order.created_at)} via {PAYMENT_LABELS[order.payment_method] || order.payment_method}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <OrderReceiptButton orderId={order.id} orderNumber={order.order_number} />
          <span data-testid="client-order-status" className={`rounded-full border px-3 py-1.5 text-xs font-bold ${status.className}`}>
            {status.label}
            {delivered && delivery.delivered_at && (
              <span className="ml-1 hidden font-normal sm:inline">· le {formatDateTime(delivery.delivered_at)}</span>
            )}
          </span>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,340px)_minmax(0,1fr)]">
        {/* -------- colonne gauche -------- */}
        <div className="space-y-4">
          {order.payment_status !== "paid" && (
            order.status === "payment_review" ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="client-payment-review-card">
                <p className="flex items-center gap-2 text-sm font-bold text-amber-800">
                  <Hourglass className="h-4 w-4" /> Paiement en cours de vérification
                </p>
                <p className="mt-1 text-xs leading-relaxed text-amber-700">
                  Vous avez déclaré un paiement via {order.payment_claim?.method_name || "votre moyen de paiement"}
                  {order.payment_claim?.transaction_id ? ` (Réf : ${order.payment_claim.transaction_id})` : ""}.
                  Notre équipe le vérifie et vous serez notifié dès sa confirmation.
                </p>
              </div>
            ) : (
              <div className={`rounded-2xl border p-4 ${order.status === "payment_rejected" ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`} data-testid="client-payment-cta-card">
                {order.status === "payment_rejected" ? (
                  <>
                    <p className="flex items-center gap-2 text-sm font-bold text-rose-700">
                      <XCircle className="h-4 w-4" /> Paiement rejeté
                    </p>
                    {order.payment_claim?.rejection_reason && (
                      <p className="mt-1 text-xs text-rose-600">Motif : {order.payment_claim.rejection_reason}</p>
                    )}
                  </>
                ) : (
                  <p className="flex items-center gap-2 text-sm font-bold text-amber-800">
                    <CreditCard className="h-4 w-4" /> Commande en attente de paiement
                  </p>
                )}
                <Link
                  to={`/paiement/${order.order_number}`}
                  data-testid="client-pay-order-btn"
                  className="mt-3 flex items-center justify-center gap-2 rounded-full bg-brand px-5 py-2.5 text-xs font-bold text-white hover:bg-brand/90"
                >
                  <CreditCard className="h-3.5 w-3.5" />
                  {order.status === "payment_rejected" ? "Refaire le paiement" : "Effectuer le paiement"} — {formatPrice(order.total, "XOF")}
                </Link>
              </div>
            )
          )}
          {order.digital_delivery && (order.digital_delivery.items_count > 0 || order.digital_delivery.status === "released") && (
            order.digital_delivery.status === "released" ? (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="client-digital-delivery-card">
                <p className="flex items-center gap-2 text-sm font-bold text-emerald-800">
                  <PackageCheck className="h-4 w-4" /> Commande validée
                </p>
                <p className="mt-1 text-xs leading-relaxed text-emerald-700">
                  Votre livraison numérique est disponible ({order.digital_delivery.items_count} élément{order.digital_delivery.items_count > 1 ? "s" : ""}).
                </p>
                <Link
                  to={`/compte/commandes/${order.id}/telechargement`}
                  data-testid="client-download-btn"
                  className="mt-3 flex items-center justify-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-emerald-700"
                >
                  <Download className="h-3.5 w-3.5" /> Télécharger
                </Link>
              </div>
            ) : (
              <div className="rounded-2xl border border-line bg-slate-50 p-4" data-testid="client-digital-delivery-locked">
                <p className="flex items-center gap-2 text-sm font-bold text-slate-500">
                  <Lock className="h-4 w-4" /> Téléchargement verrouillé
                </p>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">
                  Le lien de téléchargement se débloque automatiquement dès la confirmation de votre paiement.
                </p>
                <button
                  type="button"
                  disabled
                  data-testid="client-download-btn-disabled"
                  className="mt-3 flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-full bg-slate-200 px-5 py-2.5 text-xs font-bold text-slate-400"
                >
                  <Lock className="h-3.5 w-3.5" /> Télécharger
                </button>
              </div>
            )
          )}

          <Card icon={FileText} title="Résumé de la commande">
            <Row label="Numéro de commande" value={order.order_number} />
            <Row label="Numéro de suivi" value={order.tracking_number} />
            <Row label="Date" value={formatDateTime(order.created_at)} />
            <Row label="Méthode de paiement" value={PAYMENT_LABELS[order.payment_method] || order.payment_method} />
            <Row label="Statut" value={<span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${status.className}`}>{status.label}</span>} />
            <Row label="Sous-total" value={formatPrice(order.subtotal, "XOF")} />
            {order.discount > 0 && <Row label="Réduction" value={`- ${formatPrice(order.discount, "XOF")}`} />}
            {order.fees > 0 && <Row label="Frais" value={formatPrice(order.fees, "XOF")} />}
            <div className="mt-2 flex justify-between border-t border-line pt-2.5">
              <span className="text-xs font-bold text-ink">Total payé</span>
              <span className="font-display text-lg font-black text-brand">{formatPrice(order.total, "XOF")}</span>
            </div>
          </Card>

          <Card icon={Package} title={`Articles commandés (${order.items?.length || 0})`}>
            {(order.items || []).map((item) => (
              <div key={item.product_id} className="flex items-center gap-3 border-b border-line py-2.5 last:border-0">
                <img src={item.image_url} alt="" className="h-10 w-10 rounded-lg border border-line object-contain p-1" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-bold text-ink">{item.name}</p>
                  <p className="text-[11px] text-slate-500">Quantité : {item.quantity}</p>
                </div>
                <span className="text-xs font-bold text-ink">{formatPrice(item.line_total, "XOF")}</span>
              </div>
            ))}
          </Card>

          {allFields.length > 0 && (
            <Card icon={ClipboardList} title="Informations fournies lors de la commande">
              {allFields.map((entry, i) => (
                <div key={i} className="border-b border-line py-2 last:border-0" data-testid={`client-fields-${i}`}>
                  {allFields.length > 1 && <p className="mb-1 text-[10px] font-bold uppercase text-slate-400">{entry.product_name}</p>}
                  {entry.fields.map((field) => (
                    <Row key={field.key} label={field.label} value={field.value} />
                  ))}
                </div>
              ))}
            </Card>
          )}

          <Card icon={Truck} title="Informations de livraison">
            <Row label="Mode de livraison" value="Livraison électronique / manuelle" />
            <Row label="Délai estimé" value={(order.licenses || [])[0]?.delivery_time || "Immédiat après traitement"} />
            <Row label="Email de livraison" value={order.customer?.email} />
            {order.tracking_number && <Row label="Numéro de suivi" value={order.tracking_number} />}
          </Card>

          {(order.status_history || []).length > 0 && (
            <Card icon={Clock} title="Suivi de la commande">
              <ol className="space-y-2.5" data-testid="client-order-timeline">
                {[...order.status_history].reverse().map((h, i) => (
                  <li key={i} className="flex gap-2.5 text-xs">
                    <span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${i === 0 ? "bg-brand" : "bg-slate-300"}`} />
                    <div className="min-w-0">
                      <p className="font-bold text-ink">{h.label || h.status}</p>
                      {h.note && <p className="text-slate-500">{h.note}</p>}
                      <p className="text-[10px] text-slate-400">{formatDateTime(h.at)}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          )}
        </div>

        {/* -------- colonne droite : livraison -------- */}
        <div className="rounded-2xl border border-line bg-white p-5 sm:p-6" data-testid="client-delivery-section">
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            <Gift className="h-4 w-4 text-brand" /> Votre livraison
          </p>
          <p className="mt-0.5 text-[11px] text-slate-500">Voici les informations que nous avons préparées pour vous.</p>

          {delivered ? (
            <div className="mt-4 space-y-4">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="delivery-delivered-banner">
                <p className="flex items-center gap-2 text-sm font-bold text-emerald-800">
                  <CheckCircle2 className="h-4 w-4" /> Votre commande a été livrée
                </p>
                <p className="mt-1 text-xs text-emerald-700">
                  Merci pour votre achat. Veuillez trouver ci-dessous les informations concernant votre commande.
                </p>
              </div>

              <div className="rounded-xl border border-line bg-surface p-5">
                {delivery.title && (
                  <h2 className="font-display text-lg font-extrabold text-ink" data-testid="delivery-client-title">
                    {delivery.title}
                  </h2>
                )}
                {delivery.content && (
                  <RichTextBlocks
                    text={delivery.content}
                    className="mt-3 text-sm leading-relaxed text-slate-600"
                  />
                )}
                {(delivery.images || []).length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Images jointes</p>
                    <div className="mt-2 grid grid-cols-2 gap-2.5 sm:grid-cols-3" data-testid="delivery-client-images">
                      {delivery.images.map((img) => (
                        <a key={img} href={fileUrl(img)} target="_blank" rel="noreferrer">
                          <img src={fileUrl(img)} alt="" className="h-28 w-full rounded-xl border border-line object-cover transition-transform duration-200 hover:scale-[1.02]" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                {(delivery.files || []).length > 0 && (
                  <div className="mt-4">
                    <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Documents joints (PDF, fichiers)</p>
                    <div className="mt-2 space-y-2" data-testid="delivery-client-files">
                      {delivery.files.map((f, i) => (
                        <a
                          key={`${f.url}-${i}`}
                          href={`${fileUrl(f.url)}?download=1`}
                          data-testid={`delivery-file-${i}`}
                          className="group flex items-center gap-3 rounded-xl border border-line bg-white p-3 transition-colors duration-200 hover:border-brand"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
                            <FileText className="h-4 w-4" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-xs font-bold text-ink">{f.name || "Document"}</span>
                            <span className="block text-[10px] uppercase text-slate-400">
                              {f.ext || "fichier"}{f.size ? ` · ${Math.max(1, Math.round(f.size / 1024))} Ko` : ""}
                            </span>
                          </span>
                          <Download className="h-4 w-4 shrink-0 text-slate-400 group-hover:text-brand" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {order.reception_confirmed ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="reception-confirmed-banner">
                  <p className="flex items-center gap-2 text-sm font-bold text-emerald-800">
                    <PackageCheck className="h-4 w-4" /> Réception confirmée le {formatDateTime(order.reception_confirmed_at)}
                  </p>
                  {order.reception_comment && (
                    <p className="mt-1 text-xs italic text-emerald-700">« {order.reception_comment} »</p>
                  )}
                </div>
              ) : (
                <div className="rounded-xl border-2 border-dashed border-emerald-300 bg-white p-4" data-testid="confirm-receipt-card">
                  <p className="flex items-center gap-2 text-sm font-bold text-ink">
                    <PackageCheck className="h-4 w-4 text-emerald-600" /> Avez-vous bien reçu votre commande ?
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    Confirmez la réception pour clôturer votre commande. Vous pouvez laisser un commentaire (facultatif).
                  </p>
                  <textarea
                    data-testid="confirm-receipt-comment"
                    rows={2}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Un commentaire sur votre livraison ? (facultatif)"
                    className="mt-3 w-full rounded-lg border border-line p-3 text-xs focus:border-brand focus:outline-none"
                  />
                  <button
                    data-testid="confirm-receipt-btn"
                    onClick={confirmReceipt}
                    disabled={confirming}
                    className="mt-2 flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-emerald-700 disabled:opacity-60"
                  >
                    {confirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    J'ai bien reçu ma commande
                  </button>
                </div>
              )}
              <OrderReviewsCard order={order} />
            </div>
          ) : order.digital_delivery?.status === "released" ? (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-5" data-testid="delivery-released-banner">
              <p className="flex items-center gap-2 text-sm font-bold text-emerald-800">
                <CheckCircle2 className="h-4 w-4" /> Votre livraison numérique est disponible
              </p>
              <p className="mt-1 text-xs leading-relaxed text-emerald-700">
                Votre commande a été validée par l'administrateur. Accédez à vos fichiers, liens et clés depuis la page de téléchargement.
              </p>
              <Link
                to={`/compte/commandes/${order.id}/telechargement`}
                data-testid="delivery-goto-downloads-btn"
                className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white transition-colors duration-200 hover:bg-emerald-700"
              >
                <Download className="h-3.5 w-3.5" /> Accéder à mes téléchargements
              </Link>
            </div>
          ) : (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-5" data-testid="delivery-pending-banner">
              <p className="flex items-center gap-2 text-sm font-bold text-amber-800">
                <Clock className="h-4 w-4" /> Votre commande est actuellement en cours de traitement
              </p>
              <p className="mt-1 text-xs leading-relaxed text-amber-700">
                Notre équipe prépare votre livraison. Le contenu apparaîtra ici dès qu'elle sera validée
                {(order.licenses || [])[0]?.delivery_time ? ` (délai estimé : ${order.licenses[0].delivery_time})` : ""}.
              </p>
            </div>
          )}

          {keys.length > 0 && (
            <div className="mt-4 space-y-3">
              {keys.map((license, i) => (
                <div key={i} className="rounded-xl border border-line bg-surface p-4">
                  <p className="text-xs font-bold text-ink">{license.product_name}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <code className="flex-1 rounded-lg border border-brand/20 bg-white px-3.5 py-2.5 font-mono text-sm font-bold tracking-wider text-brand"
                          data-testid={`detail-license-key-${i}`}>
                      {license.key_value}
                    </code>
                    <button onClick={() => copy(license.key_value)} data-testid={`detail-copy-key-${i}`}
                            className="flex items-center gap-1.5 rounded-lg bg-ink px-3.5 py-2.5 text-xs font-bold text-white hover:bg-brand">
                      <Copy className="h-3.5 w-3.5" /> Copier
                    </button>
                  </div>
                  {license.instructions && (
                    <div className="mt-2 text-[11px] leading-snug text-slate-500">
                      <RichTextBlocks text={license.instructions} className="text-[11px]" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {legacyMessages.map((license, i) => (
            <div key={i} className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs font-bold text-emerald-800">{license.product_name}</p>
              <RichTextBlocks text={license.admin_message} className="mt-1.5 text-xs leading-relaxed text-emerald-800" />
              {license.credentials && (
                <code className="mt-2 block rounded-md bg-white px-3 py-2 font-mono text-xs font-bold text-emerald-700">
                  {license.credentials}
                </code>
              )}
            </div>
          ))}

          <p className="mt-5 rounded-xl bg-brand-soft/60 p-3.5 text-[11px] leading-snug text-slate-600">
            ❓ Vous avez une question concernant cette commande ? Contactez notre équipe support depuis la page{" "}
            <Link to="/compte/tickets" className="font-bold text-brand underline">Tickets support</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
